import { uniqueId } from "lodash";

import { appApplicationGroups, strCode } from "../../../../consts";
import { changeHistoryTypes } from "../consts/changeHistoryTypes";
import { attributeNames, attributes } from "../consts/attributeNames";
import { t } from "../../../../services";
import { appsUtil, arrayUtil, dateUtil, locationUtil, objectUtil } from "../../../../utils";

const changeTypes = {
  addition: "addition",
  update: "update",
  deletion: "deletion"
};

const getBaseInfo = (logItem) => {
  const { adminEmail, timestamp, type } = logItem;
  return { author: adminEmail, timestamp, type };
};

/**
 * Helper function that transforms a single attributeChange object into an
 * object with more convinient structure
 *
 * @param {object} attributeChange  attributeChange object to be transformed
 * @returns {object}                transformed attributeChange object
 */
const parseAttributeChange = (attributeChange) => {
  let changeType = changeTypes.update;
  let { oldValue, newValue } = attributeChange;

  if (Array.isArray(oldValue) && Array.isArray(newValue)) {
    const commonValues = arrayUtil.getCommonValues(newValue, oldValue);

    newValue = newValue.filter((value) => !commonValues.includes(value));
    oldValue = oldValue.filter((value) => !commonValues.includes(value));
  }

  if (oldValue == null || oldValue?.length === 0) {
    changeType = changeTypes.addition;
  }

  if (newValue == null || newValue?.length === 0) {
    changeType = changeTypes.deletion;
  }

  return {
    change: {
      ...attributeChange,
      newValue,
      oldValue
    },
    changeType
  };
};

/**
 * Transforms list of attributesChanges property of logItem into a
 * list with more convinient structure
 *
 * @param {object} logItem  log item with attributesChanges list to be transformed
 * @returns {object[]}      a list of transformed attributesChanges for logItem
 */
const parseAttributeChanges = (logItem) => {
  return logItem.attributesChanges.map((attributeChange) => {
    const changeObject = parseAttributeChange(attributeChange);

    return {
      ...getBaseInfo(logItem),
      appType: logItem?.appType,
      name: logItem?.name,
      ...changeObject
    };
  });
};

/**
 * Finds chnages from added and removed arrays that has the same application and role values,
 * since such changes has to be parsed differently
 *
 * @param {object[]} added    a list of added changes from groupsChanges property on a logItem
 * @param {object[]} removed  a list of removed changes from groupsChanges property on a logItem
 * @returns {object}          an object that consists of commonAppRoles list, that contains objects
 *                            with appType and name property, which are common across added and
 *                            removed array, and commonLogs, that contains a list of logs with the
 *                            detailed changes info for the common items
 */
const getCommonAppRoles = (added, removed) => {
  const commonAppRoles = [];
  const commonLogs = [];

  if (added?.length && removed?.length) {
    added.forEach((addedItem) => {
      const removedItem = removed.find(
        (removedItem) =>
          removedItem.appType === addedItem.appType && removedItem.name === addedItem.name
      );

      if (removedItem) {
        commonLogs.push({ added: addedItem, removed: removedItem });
        commonAppRoles.push({ appType: addedItem.appType, name: addedItem.name });
      }
    });
  }

  return { commonAppRoles, commonLogs };
};

/**
 * Transformes a list of item as a list of attributeChange objects
 *
 * @param {object[]} commonLogs a list of common log items that should be parsed as attributeChanges
 * @returns {object[]}          a transformed list of attributeChange objects
 */
const getCommonLogsAttributesChanges = (commonLogs = []) => {
  const attributesChanges = [];

  commonLogs.forEach((commonItem) => {
    const { added, removed } = commonItem;

    Object.keys(added).forEach((attribute) => {
      if (added[attribute] !== removed[attribute]) {
        let newValue = added[attribute];
        let oldValue = removed[attribute];

        const attributeChangeData = {
          appType: added?.appType,
          name: added?.name
        };

        if (attribute === attributes.markets) {
          const commonMarkets = arrayUtil.getCommonValues(newValue, oldValue);

          newValue = newValue.filter((market) => !commonMarkets.includes(market));
          oldValue = oldValue.filter((market) => !commonMarkets.includes(market));

          newValue.forEach((market) => {
            attributesChanges.push({
              ...attributeChangeData,
              attributeChange: {
                name: attribute,
                newValue: market,
                oldValue: null
              }
            });
          });

          oldValue.forEach((market) => {
            attributesChanges.push({
              ...attributeChangeData,
              attributeChange: {
                name: attribute,
                newValue: null,
                oldValue: market
              }
            });
          });
        } else {
          attributesChanges.push({
            ...attributeChangeData,
            attributeChange: {
              name: attribute,
              newValue,
              oldValue
            }
          });
        }
      }
    });
  });

  return attributesChanges;
};

/**
 * Helper function for removing items from added and removed arrays, that has application and
 * role combinations provided as a parameter
 *
 * @param {object[]} added          a list of added changes from groupsChanges property on a logItem
 * @param {object[]} removed        a list of removed changes from groupsChanges property on a logItem
 * @param {object[]} commonAppRoles a list of common application an role combinations, that should
 *                                  be filtered out of added and removed arrays
 * @returns {object}                an object with filteredAdded and filteredRemoved lists without commonAppRoles
 */
const filterGroupsChanges = (added = [], removed = [], commonAppRoles = []) => {
  const hasCommonAppRole = (item) =>
    commonAppRoles.find(
      (commonItem) => commonItem.appType === item.appType && commonItem.name === item.name
    );

  const filteredAdded = added.filter((item) => !hasCommonAppRole(item));
  const filteredRemoved = removed.filter((item) => !hasCommonAppRole(item));

  return { filteredAdded, filteredRemoved };
};

/**
 * Transforms groupsChanges property of logItem into a
 * list of singe attributeChange objects
 *
 * @param {object} logItem  log item with groupsChanges property to be transformed
 * @returns {object[]}      a list of single attributeChange objects of log item groupsChanges
 */
const parseGroupsChanges = (logItem) => {
  const result = [];
  const { added, changed, removed } = logItem.groupsChanges;

  if (changed?.length) {
    changed.map((item) => {
      const items = parseAttributeChanges(item).map((item) => {
        return { ...item, ...getBaseInfo(logItem) };
      });

      result.push(...items);
    });
  }

  const { commonAppRoles = [], commonLogs = [] } = getCommonAppRoles(added, removed);

  if (commonLogs) {
    const attributesChanges = getCommonLogsAttributesChanges(commonLogs);

    const parsedChanges = attributesChanges.map((attributeChangeItem) => {
      const parsed = parseAttributeChange(attributeChangeItem.attributeChange);
      return {
        ...getBaseInfo(logItem),
        appType: attributeChangeItem?.appType,
        name: attributeChangeItem?.name,
        ...parsed
      };
    });

    result.push(...parsedChanges);
  }

  const { filteredAdded, filteredRemoved } = filterGroupsChanges(added, removed, commonAppRoles);

  const getAppTypeChangeObject = (item, changeType = changeTypes.addition) => {
    return {
      ...getBaseInfo(logItem),
      appType: item.appType,
      name: item.name,
      changeType: changeType,
      change: {
        name: attributes.appType,
        newValue: changeType === changeTypes.addition ? item.appType : null,
        oldValue: changeType === changeTypes.deletion ? item.appType : null
      }
    };
  };

  if (filteredAdded?.length) {
    filteredAdded.map((item) => {
      const changeObject = getAppTypeChangeObject(item);
      result.push(changeObject);
    });
  }

  if (filteredRemoved?.length) {
    filteredRemoved.map((item) => {
      const changeObject = getAppTypeChangeObject(item, changeTypes.deletion);
      result.push(changeObject);
    });
  }

  return result;
};

/**
 * Transforms logs, received from the backend, into more convinient structure,
 * leaving only necessary information
 *
 * @param {object[]} logs   list of logs, fetched from the backend
 * @returns {object[]}      transformed logs
 */
const parseLogs = (logs) => {
  const parsedLogList = [];

  logs.forEach((logItem) => {
    if (
      [
        changeHistoryTypes.userCreation,
        changeHistoryTypes.userPasswordReset,
        changeHistoryTypes.userDeletion
      ].includes(logItem?.type)
    ) {
      parsedLogList.push(getBaseInfo(logItem));
      return;
    }

    if (logItem?.attributesChanges?.length) {
      const items = parseAttributeChanges(logItem);
      parsedLogList.push(...items);
    }

    if (logItem?.groupsChanges) {
      const items = parseGroupsChanges(logItem);
      parsedLogList.push(...items);
    }

    logItem.id = uniqueId();
  });

  return parsedLogList;
};

const getAdminRoleName = (role) => {
  return appApplicationGroups.find((item) => item.group === role)?.value ?? role;
};

/**
 * Helper function for formatting a value for specific attributes, which will be
 * put in the result string
 *
 * @param {object} value      value to be transformed
 * @param {string} attribute  attribute name
 * @returns {string}          transformed value
 */
const formatValue = (value, attribute) => {
  if ([attributes.activeFrom, attributes.activeTo].includes(attribute)) {
    return dateUtil.formatDateFromTimestamp(value, false);
  }

  if (attribute === attributes.locationId) {
    return locationUtil.getLocationNameById(value);
  }

  if ([attributes.appType, attributes.appTypes].includes(attribute)) {
    if (Array.isArray(value)) {
      const appNames = value.map((appType) => {
        return appsUtil.getAppName(appType);
      });

      return appNames.join(", ");
    } else {
      return appsUtil.getAppName(value);
    }
  }

  if (attribute === attributes.roles) {
    if (Array.isArray(value)) {
      const roles = [];

      value.forEach((role) => {
        roles.push(getAdminRoleName(role));
      });

      return roles.join(", ");
    }

    return getAdminRoleName(value);
  }

  if (Array.isArray(value)) {
    const values = value.map((item) => {
      if (objectUtil.isObject(item)) {
        return JSON.stringify(item);
      }
      return item;
    });

    return values.join(", ");
  }

  if (objectUtil.isObject(value)) {
    return JSON.stringify(value);
  }

  return value;
};

const getAttributeName = (attribute) => {
  return t(attributeNames[attribute] ?? attribute);
};

/**
 * Helper function for generating an action string for a logItem
 * that represents changes in user attributes
 *
 * @param {object[]} logItem  log item, transformed using parseLogs function
 * @returns {string}          text description of logItem action
 */
const getChangeInfo = (logItem) => {
  const { appType, name, change, changeType } = logItem;
  const { name: attribute, oldValue, newValue } = change;

  let changeStr = "";

  switch (changeType) {
    case changeTypes.update:
      if (attribute === attributes.canUseIntelligentDashboards) {
        changeStr = t(
          newValue === "true"
            ? strCode.ENABLED_ATTRIBUTE_ACTION
            : strCode.DISABLED_ATTRIBUTE_ACTION,
          { attributeName: getAttributeName(attribute) }
        );

        return changeStr;
      }

      changeStr = t(strCode.CHANGED_ATTRIBUTE_ACTION, {
        attributeName: getAttributeName(attribute),
        oldValue: formatValue(oldValue, attribute),
        newValue: formatValue(newValue, attribute)
      });
      if (appType && name) {
        changeStr += t(strCode.APPLICATION_ACTION_INFO, {
          application: appsUtil.getAppName(appType),
          role: name
        });
      }

      return changeStr;

    case changeTypes.addition:
      changeStr = t(strCode.ADDED_ATTRIBUTE_ACTION, {
        attributeName: getAttributeName(attribute),
        newValue: formatValue(newValue, attribute)
      });

      if (attribute === attributes.appType) {
        changeStr += t(strCode.ROLE_ACTION_INFO, { role: name });
      } else {
        if (appType && name) {
          changeStr += t(strCode.APPLICATION_ACTION_INFO, {
            application: appsUtil.getAppName(appType),
            role: name
          });
        }
      }

      return changeStr;

    case changeTypes.deletion:
      changeStr = t(strCode.REMOVED_ATTRIBUTE_ACTION, {
        attributeName: getAttributeName(attribute),
        oldValue: formatValue(oldValue, attribute)
      });

      if (attribute === attributes.appType) {
        changeStr += t(strCode.ROLE_ACTION_INFO, { role: name });
      } else {
        if (appType && name) {
          changeStr += t(strCode.APPLICATION_REMOVE_ACTION_INFO, {
            application: appsUtil.getAppName(appType),
            role: name
          });
        }
      }
      return changeStr;
  }

  return changeStr;
};

/**
 * Generates string with action description based on received logItem
 *
 * @param {object} logItem  log item, transformed using parseLogs function
 * @returns {string}        text description of logItem action
 */
const getLogAction = (logItem) => {
  if (logItem?.type === changeHistoryTypes.userCreation) {
    return t(strCode.ACCOUNT_CREATED_ACTION);
  }

  if (logItem?.type === changeHistoryTypes.userPasswordReset) {
    return t(strCode.PASSWORD_RESET_ACTION);
  }

  if (logItem?.type === changeHistoryTypes.userDeletion) {
    return t(strCode.ACCOUNT_DELETED_ACTION);
  }

  return getChangeInfo(logItem);
};

export const historyLogsParserUtils = {
  parseLogs,
  getLogAction
};
